const STORAGE_KEY = 'hive.auth.returnUrl';

/**
 * Where the user was when their session ended.
 *
 * Signing out goes through Keycloak, which is a full-page redirect: every signal in memory is gone
 * by the time they come back. `sessionStorage` is the narrowest thing that survives it — it lives
 * for the tab, so a second tab cannot steal the destination and closing the browser forgets it.
 *
 * Only paths within this app are ever stored or returned. A return URL is attacker-controllable in
 * general (it rides in a query string), and following an absolute one after authentication is an
 * open redirect: the user signs in on a page they trust and is handed to one they do not.
 */
export function rememberReturnUrl(url: string): void {
  const safe = asInternalPath(url);
  if (safe === null) {
    return;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, safe);
  } catch {
    // Private mode, or storage disabled. Losing the destination is a worse landing, not a failure.
  }
}

/** Reads and clears in one step, so a stale destination cannot be followed twice. */
export function takeReturnUrl(): string | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return stored === null ? null : asInternalPath(stored);
  } catch {
    return null;
  }
}

export function forgetReturnUrl(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the value is unreachable either way.
  }
}

/**
 * A single leading slash and no scheme. `//evil.test` is protocol-relative and a browser reads it as
 * another origin, so the second character matters as much as the first.
 */
export function asInternalPath(url: string): string | null {
  const trimmed = url.trim();

  if (
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('/\\')
  ) {
    return null;
  }

  return trimmed;
}
